import { Component, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatPaginator } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import Swal from 'sweetalert2';
import { AuthService } from '../../../core/auth.service';
import { StaffService } from '../staff.service';

@Component({
  selector: 'app-staff-requests',
  templateUrl: './staff-requests.component.html',
  styleUrls: ['./staff-requests.component.scss']
})
export class StaffRequestsComponent implements OnInit {
  requestForm: FormGroup;
  myRequestsDataSource = new MatTableDataSource<any>([]);
  approvalDataSource = new MatTableDataSource<any>([]);
  requestTargets: any[] = [];
  loading = false;

  myColumns = ['requestedAt', 'requestType', 'title', 'directedTo', 'amount', 'period', 'status', 'reviewComment'];
  approvalColumns = ['requestedAt', 'staffName', 'requestType', 'title', 'directedTo', 'amount', 'status', 'actions'];

  @ViewChild('myPaginator') myPaginator!: MatPaginator;
  @ViewChild('approvalPaginator') approvalPaginator!: MatPaginator;

  constructor(
    private fb: FormBuilder,
    private service: StaffService,
    private auth: AuthService
  ) {
    this.requestForm = this.fb.group({
      requestType: ['Leave', Validators.required],
      title: ['', Validators.required],
      description: [''],
      amount: [null],
      startDate: [''],
      endDate: [''],
      directedToStaffId: [null, Validators.required]
    });

    this.requestForm.get('requestType')?.valueChanges.subscribe(() => this.applyTypeRules());
  }

  ngOnInit(): void {
    this.loadData();
  }

  loadData(): void {
    this.loading = true;
    if (this.canCreateRequest()) {
      this.service.getRequestTargets().subscribe({
        next: (rows) => (this.requestTargets = rows || []),
        error: () => (this.requestTargets = [])
      });
    } else {
      this.requestTargets = [];
    }

    this.service.getMyRequests().subscribe({
      next: (rows) => {
        this.myRequestsDataSource.data = rows || [];
        this.myRequestsDataSource.paginator = this.myPaginator;
        this.loading = false;
      },
      error: (err) => {
        this.loading = false;
        Swal.fire('Error', err?.error?.message || 'Failed to load your requests.', 'error');
      }
    });

    if (this.canApprove()) {
      this.service.getAllRequests({ status: 'Pending' }).subscribe({
        next: (rows) => {
          this.approvalDataSource.data = rows || [];
          this.approvalDataSource.paginator = this.approvalPaginator;
        },
        error: () => {
          this.approvalDataSource.data = [];
        }
      });
    } else {
      this.approvalDataSource.data = [];
    }
  }

  submitRequest(): void {
    this.applyTypeRules();
    if (this.requestForm.invalid) {
      this.requestForm.markAllAsTouched();
      return;
    }

    const payload = this.requestForm.value;
    this.service.createRequest(payload).subscribe({
      next: () => {
        Swal.fire('Submitted', 'Request submitted for approval.', 'success');
        this.requestForm.reset({
          requestType: 'Leave',
          title: '',
          description: '',
          amount: null,
          startDate: '',
          endDate: '',
          directedToStaffId: null
        });
        this.loadData();
      },
      error: (err) => Swal.fire('Error', err?.error?.message || 'Failed to submit request.', 'error')
    });
  }

  canApprove(): boolean {
    return this.auth.hasAnyPermission(['staff.request.approve', 'staff.manage']);
  }

  canCreateRequest(): boolean {
    return this.auth.hasAnyPermission(['staff.request.create', 'staff.manage']);
  }

  approve(row: any): void {
    Swal.fire({
      title: 'Approve Request?',
      input: 'text',
      inputLabel: 'Comment (optional)',
      showCancelButton: true,
      confirmButtonText: 'Approve'
    }).then((r) => {
      if (!r.isConfirmed) return;
      this.service.approveRequest(row.id, r.value || null).subscribe({
        next: () => {
          Swal.fire('Approved', 'Request approved successfully.', 'success');
          this.loadData();
        },
        error: (err) => Swal.fire('Error', err?.error?.message || 'Failed to approve.', 'error')
      });
    });
  }

  reject(row: any): void {
    Swal.fire({
      title: 'Reject Request?',
      input: 'text',
      inputLabel: 'Reason / comment',
      inputValidator: (value) => (!value ? 'Comment is required for rejection.' : null),
      showCancelButton: true,
      confirmButtonText: 'Reject'
    }).then((r) => {
      if (!r.isConfirmed) return;
      this.service.rejectRequest(row.id, r.value || null).subscribe({
        next: () => {
          Swal.fire('Rejected', 'Request rejected.', 'success');
          this.loadData();
        },
        error: (err) => Swal.fire('Error', err?.error?.message || 'Failed to reject.', 'error')
      });
    });
  }

  get isFundsOrLoan(): boolean {
    const type = this.requestForm.get('requestType')?.value;
    return type === 'Funds' || type === 'Loan';
  }

  get isLeave(): boolean {
    return this.requestForm.get('requestType')?.value === 'Leave';
  }

  private applyTypeRules(): void {
    const type = this.requestForm.get('requestType')?.value;
    const amountCtrl = this.requestForm.get('amount');
    const startCtrl = this.requestForm.get('startDate');
    const endCtrl = this.requestForm.get('endDate');

    if (type === 'Leave') {
      amountCtrl?.setValue(null);
      startCtrl?.setValidators([Validators.required]);
      endCtrl?.setValidators([Validators.required]);
      amountCtrl?.clearValidators();
    } else if (type === 'Funds' || type === 'Loan') {
      startCtrl?.setValue('');
      endCtrl?.setValue('');
      startCtrl?.clearValidators();
      endCtrl?.clearValidators();
      amountCtrl?.setValidators([Validators.required]);
    } else {
      amountCtrl?.setValue(null);
      startCtrl?.setValue('');
      endCtrl?.setValue('');
      amountCtrl?.clearValidators();
      startCtrl?.clearValidators();
      endCtrl?.clearValidators();
    }

    amountCtrl?.updateValueAndValidity({ emitEvent: false });
    startCtrl?.updateValueAndValidity({ emitEvent: false });
    endCtrl?.updateValueAndValidity({ emitEvent: false });
  }
}
